/**
 * Web field-box styling and markers for app-owned search inputs.
 *
 * See `searchFieldWeb.ts` for why this is a platform-extension pair. webpack
 * resolves this file; Metro resolves the no-op, so nothing here can reach
 * Android.
 *
 * The markers are the important part. globalStyles.web.ts paints the focus
 * ring on the element carrying `data-zen-field-box` and suppresses it on the
 * element carrying `data-zen-field`, which is what stops the browser drawing a
 * second rectangle inside an already-bordered search container (14.1, 14.2).
 */

import {StyleProp, TextStyle, ViewStyle} from 'react-native';

import {webColor, webControl, webFontFamily, webSearch} from './webTokens';

/**
 * One border, one radius, one height for every search field on web (14.11).
 * Applied *after* a call site's own container styling and *before* an
 * explicit height prop, so a screen that deliberately sizes its field still
 * wins while the incidental differences are flattened.
 */
export const webSearchBox: StyleProp<ViewStyle> = {
  height: webSearch.height,
  borderRadius: webSearch.borderRadius,
  borderWidth: webSearch.borderWidth,
  /* A width without a colour resolves to currentColor - a black box. */
  borderColor: webColor.border,
  gap: webSearch.gap,
};

/**
 * `top: -5` / `top: 3` offsets in the native search bars compensate for the
 * way Android centres a TextInput's line box. On web the flex row already
 * centres it, so the same offsets just push the text off-centre - which is
 * the misaligned search text in the screenshots. Zeroing them (and the
 * default vertical padding the browser gives an <input>) puts the caret back
 * on the middle line.
 */
export const webSearchInput: StyleProp<TextStyle> = {
  top: 0,
  paddingVertical: 0,
  fontSize: webSearch.fontSize,
  fontFamily: webFontFamily.regular,
  borderWidth: 0,
  outlineWidth: 0,
};

/**
 * The wrapper CustomSearchBar puts between the box and the input so it can
 * overlay a fake placeholder. It carries the same Android line-box nudge, so
 * it needs the same correction.
 */
export const webSearchInnerColumn: StyleProp<ViewStyle> = {top: 0};

/** Keeps a faked placeholder on the same baseline and x-position as the text. */
export const webSearchPlaceholder: StyleProp<ViewStyle> = {
  top: 0,
  bottom: 0,
  left: 0,
};

export const fieldBoxProps = {dataSet: {zenFieldBox: 'search'}};

export const fieldInputProps = {dataSet: {zenField: 'true'}};

/**
 * For the handful of bordered field boxes a screen builds itself rather
 * than taking from ZenInput - the SKU field on Add Product is the one in
 * this form. Applied with fieldBoxProps and fieldInputProps, it gives such
 * a field the same height, radius and focus behaviour as every other input
 * on the page (16.6), including a single focus ring on the box instead of a
 * second rectangle drawn inside it (15.7).
 */
export const webFieldBox: StyleProp<ViewStyle> = {
  height: webControl.boxHeight,
  borderRadius: webControl.borderRadius,
  borderWidth: webControl.borderWidth,
};